import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Upload, Loader2, CheckCircle2, FileText, Shield, Lock
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import Logo from './ui/Logo';
import { formatCPF, unformatCPF } from '../lib/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SolicitacaoDocumentosPage = () => {
  const { token } = useParams();
  const [step, setStep] = useState('auth');
  const [cpf, setCpf] = useState('');
  const [senha, setSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [solicitacao, setSolicitacao] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleAuth = async (e) => {
    e.preventDefault();
    if (!cpf || !senha) {
      toast.error('Preencha o CPF e a senha');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/documentos/solicitacoes/acessar/${token}`, {
        cpf: unformatCPF(cpf),
        senha: senha.trim()
      });
      setSolicitacao(r.data);
      setStep('upload');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Link inválido ou credenciais incorretas');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (tipoDocId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.jpg,.jpeg,.png,.doc,.docx';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Arquivo muito grande. Máximo 10MB');
        return;
      }
      setUploading(tipoDocId);
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('token', token);
        formData.append('cpf', unformatCPF(cpf));
        formData.append('senha', senha.trim());
        await axios.post(`${API}/documentos/solicitacoes/${solicitacao.id}/upload/${tipoDocId}`, formData);
        toast.success('Documento enviado com sucesso!');
        const r = await axios.post(`${API}/documentos/solicitacoes/acessar/${token}`, { cpf: unformatCPF(cpf), senha: senha.trim() });
        setSolicitacao(r.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erro ao enviar documento');
      } finally {
        setUploading(null);
      }
    };
    input.click();
  };

  const allUploaded = solicitacao?.documentos_esperados?.every(
    (de) => solicitacao.documentos_enviados?.some(
      (dee) => dee.tipo_documento_id === de.tipo_documento_id
    )
  );

  if (step === 'auth') {
    return (
      <div className="min-h-screen w-full flex flex-col lg:flex-row brand-canvas relative overflow-hidden">
        <div className="brand-glow brand-float" style={{ top: '-6rem', left: '-4rem', width: '28rem', height: '28rem', background: '#2563EB' }} />
        <div className="brand-glow brand-float" style={{ bottom: '-8rem', left: '20%', width: '32rem', height: '32rem', background: '#00D4CB', animationDelay: '2s' }} />

        <div className="relative z-10 flex flex-1 flex-col justify-between p-8 sm:p-12 lg:p-16">
          <Logo variant="full" theme="dark" showTagline iconClassName="h-10 w-auto" wordClassName="text-2xl" />
          <div className="max-w-xl py-12 lg:py-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold text-white/80 backdrop-blur-sm">
              <Shield className="h-3.5 w-3.5 text-[#00D4CB]" />
              Portal de Documentos
            </div>
            <h1 className="mt-6 text-4xl sm:text-5xl xl:text-6xl font-extrabold leading-[1.05] tracking-tight text-white">
              Envio de
              <br />
              <span className="brand-text-gradient">documentos seguro.</span>
            </h1>
            <p className="mt-6 max-w-md text-base sm:text-lg text-white/60">
              Acesse com seu CPF e a senha enviada por e-mail para enviar os documentos solicitados pela empresa.
            </p>
          </div>
          <p className="hidden lg:block text-xs text-white/40">
            &copy; {new Date().getFullYear()} GridProfile &middot; Intelig&ecirc;ncia para Gest&atilde;o de Pessoas
          </p>
        </div>

        <div className="relative z-10 w-full lg:w-[480px] xl:w-[520px] flex items-center justify-center p-6 sm:p-10 lg:p-12 bg-[#0B1220]/40 lg:bg-[#0B1220]/60 backdrop-blur-xl lg:border-l border-white/10">
          <div className="w-full max-w-sm">
            <h2 className="text-2xl font-bold text-white">Acessar Portal</h2>
            <p className="mt-1 text-sm text-white/50">Use o CPF e a senha recebidos por e-mail</p>
            <form onSubmit={handleAuth} className="mt-8 space-y-5">
              <div>
                <Label htmlFor="cpf" className="text-[11px] font-semibold uppercase tracking-wider text-white/50">CPF</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <Input id="cpf" value={cpf} onChange={e => setCpf(formatCPF(e.target.value.replace(/\D/g, '').slice(0, 11)))}
                    placeholder="000.000.000-00"
                    className="h-12 pl-11 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#00D4CB] focus-visible:border-[#00D4CB]/50" required />
                </div>
              </div>
              <div>
                <Label htmlFor="senha" className="text-[11px] font-semibold uppercase tracking-wider text-white/50">Senha de 6 dígitos</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <Input id="senha" type="password" value={senha} onChange={e => setSenha(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="••••••"
                    className="h-12 pl-11 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#00D4CB] focus-visible:border-[#00D4CB]/50" required />
                </div>
              </div>
              <Button type="submit" disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Validando...</> : <><Shield className="mr-2 h-4 w-4" />Acessar Portal</>}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen w-full flex flex-col lg:flex-row brand-canvas relative overflow-hidden">
        <div className="brand-glow brand-float" style={{ top: '-6rem', left: '-4rem', width: '28rem', height: '28rem', background: '#2563EB' }} />
        <div className="brand-glow brand-float" style={{ bottom: '-8rem', left: '20%', width: '32rem', height: '32rem', background: '#00D4CB', animationDelay: '2s' }} />
        <div className="relative z-10 flex flex-1 flex-col justify-center items-center p-8">
          <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mb-6">
            <CheckCircle2 className="h-10 w-10 text-emerald-400" />
          </div>
          <h1 className="text-3xl font-bold text-white text-center">Documentos enviados com sucesso!</h1>
          <p className="mt-3 text-white/60 text-center max-w-md">
            Agradecemos pelo envio. A empresa irá analisar os documentos e entraremos em contato se necessário.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row brand-canvas relative overflow-hidden">
      <div className="brand-glow brand-float" style={{ top: '-6rem', left: '-4rem', width: '28rem', height: '28rem', background: '#2563EB' }} />
      <div className="brand-glow brand-float" style={{ bottom: '-8rem', left: '20%', width: '32rem', height: '32rem', background: '#00D4CB', animationDelay: '2s' }} />

      <div className="relative z-10 flex flex-1 flex-col justify-between p-8 sm:p-12 lg:p-16">
        <Logo variant="full" theme="dark" showTagline iconClassName="h-10 w-auto" wordClassName="text-2xl" />
        <div className="max-w-xl py-12 lg:py-0">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold text-white/80 backdrop-blur-sm">
            <FileText className="h-3.5 w-3.5 text-[#00D4CB]" />
            Documentos solicitados
          </div>
          <h1 className="mt-6 text-4xl sm:text-5xl xl:text-6xl font-extrabold leading-[1.05] tracking-tight text-white">
            Ol&aacute;, <span className="brand-text-gradient">{solicitacao?.nome_destinatario?.split(' ')[0]}</span>
          </h1>
          <p className="mt-6 max-w-md text-base sm:text-lg text-white/60">
            Envie os documentos solicitados abaixo. Arquivos aceitos: PDF, JPG, PNG, DOC, DOCX (m&aacute;x. 10MB cada).
          </p>
        </div>
        <p className="hidden lg:block text-xs text-white/40">
          &copy; {new Date().getFullYear()} GridProfile &middot; Intelig&ecirc;ncia para Gest&atilde;o de Pessoas
        </p>
      </div>

      <div className="relative z-10 w-full lg:w-[480px] xl:w-[520px] flex items-start justify-center p-6 sm:p-10 lg:p-12 bg-[#0B1220]/40 lg:bg-[#0B1220]/60 backdrop-blur-xl lg:border-l border-white/10 overflow-y-auto" style={{ maxHeight: '100vh' }}>
        <div className="w-full max-w-sm">
          <h2 className="text-2xl font-bold text-white">Documentos</h2>
          <p className="mt-1 text-sm text-white/50">Clique em cada item para fazer o upload</p>

          <div className="mt-6 space-y-3">
            {solicitacao?.documentos_esperados?.map((de) => {
              const enviado = solicitacao.documentos_enviados?.find(
                (dee) => dee.tipo_documento_id === de.tipo_documento_id
              );
              const aprovado = enviado?.status === 'aprovado';
              const recusado = enviado?.status === 'recusado';
              return (
                <div key={de.tipo_documento_id}
                  className={`border rounded-lg p-4 transition-colors ${
                    aprovado ? 'border-emerald-500/50 bg-emerald-500/10' :
                    recusado ? 'border-red-500/50 bg-red-500/10' :
                    enviado ? 'border-blue-500/50 bg-blue-500/10' :
                    'border-white/10 bg-white/5'
                  }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className={`h-4 w-4 ${aprovado ? 'text-emerald-400' : recusado ? 'text-red-400' : 'text-white/50'}`} />
                      <span className="text-sm font-medium text-white">{de.tipo_documento_nome}</span>
                      {de.obrigatorio && <span className="text-[10px] text-red-400">*</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      {enviado && !aprovado && !recusado && (
                        <span className="text-[10px] text-blue-400">Enviado</span>
                      )}
                      {aprovado && <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
                      {recusado && <span className="text-[10px] text-red-400">Recusado</span>}
                      <Button size="sm" variant="ghost" disabled={uploading === de.tipo_documento_id || aprovado}
                        onClick={() => handleUpload(de.tipo_documento_id)}
                        className="text-white/50 hover:text-white">
                        {uploading === de.tipo_documento_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  {recusado && enviado?.observacao_recusa && (
                    <p className="mt-2 text-xs text-red-400">{enviado.observacao_recusa}</p>
                  )}
                </div>
              );
            })}
          </div>

          {allUploaded && (
            <Button onClick={() => setSuccess(true)}
              className="w-full mt-6 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl">
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Finalizar Envio
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SolicitacaoDocumentosPage;
